import { datadogRum } from '@datadog/browser-rum';
import { setOnDeploymentOutdated } from 'src/utils/api-fetch';
import { DatadogRumAction } from 'src/utils/logging';

import { setOnClientOutdated } from '@savvy/deprecated-shared/utils/apollo-client';
import clientEnv from '@savvy/env/client';

import type { SkewOutdatedEvent, SkewReloadClock } from './controller';
import { createSkewRefreshController } from './controller';

const LAST_RELOAD_AT_KEY = 'savvy.skew.lastReloadAt';

// Per-tab by design: a multi-tab user on a broken deploy reloads once per tab, which is
// bounded. sessionStorage throws in some privacy modes, so every access is guarded —
// losing the stamp degrades to "no breaker", never to a crash on a normal request.
const browserClock: SkewReloadClock = {
  now: () => Date.now(),

  readLastReloadAt: () => {
    try {
      const raw = window.sessionStorage.getItem(LAST_RELOAD_AT_KEY);
      if (raw === null) return null;
      const parsed = Number.parseInt(raw, 10);
      return Number.isFinite(parsed) ? parsed : null;
    } catch {
      return null;
    }
  },

  writeLastReloadAt: (at) => {
    try {
      window.sessionStorage.setItem(LAST_RELOAD_AT_KEY, String(at));
    } catch {
      // No breaker stamp is survivable; blocking the reload is not.
    }
  },
};

function logOutdated(event: SkewOutdatedEvent): void {
  datadogRum.addAction(DatadogRumAction.ClientOutdated, {
    trigger: event.trigger,
    operation_name: event.operationName,
    client_deployment_id: event.clientDeploymentId,
  });
}

export const skewRefreshController = createSkewRefreshController({
  clock: browserClock,
  reload: () => window.location.reload(),
  logOutdated,
});

function clientDeploymentId(): string | null {
  const id = clientEnv.NEXT_PUBLIC_VERCEL_DEPLOYMENT_ID;
  return id !== undefined && id !== '' ? id : null;
}

/**
 * Point both skew detectors at the controller. Returns a teardown that clears both
 * registrations, so the caller's effect cleanup can't leave a stale handler behind.
 */
export function registerSkewDetectors(): () => void {
  setOnClientOutdated(({ operationName }) => {
    skewRefreshController.reportOutdated({
      trigger: 'graphql',
      operationName,
      clientDeploymentId: clientDeploymentId(),
    });
  });

  setOnDeploymentOutdated(() => {
    skewRefreshController.reportOutdated({
      trigger: 'route-handler',
      operationName: null,
      clientDeploymentId: clientDeploymentId(),
    });
  });

  return () => {
    setOnClientOutdated(null);
    setOnDeploymentOutdated(null);
  };
}

export type { SkewRefreshState } from './controller';
